import React from 'react';
import {
  Shield,
  Target,
  Users,
  FlaskConical,
  Lightbulb,
  Award,
  Building2
} from 'lucide-react';

const About = () => {
  return (
    <section
      id="about"
      style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h2 className="display-large" style={{ marginBottom: '20px' }}>
            About Ciqura Labs Indore
          </h2>
          <p
            className="body-large"
            style={{
              color: 'var(--text-secondary)',
              maxWidth: '900px',
              margin: '0 auto',
            }}
          >
            Best Ethical Hacking & AIML Training Institute in Indore with Live SOC Labs
          </p>
        </div>

        {/* Ciqur24 Trust Block */}
        <div
          style={{
            background: 'linear-gradient(90deg, rgba(0,255,209,0.12), rgba(0,255,209,0.04))',
            border: '1.5px solid var(--brand-primary)',
            padding: '36px',
            marginBottom: '70px',
            borderRadius: '14px',
            textAlign: 'center',
          }}
        >
          <Building2
            size={44}
            style={{ color: 'var(--brand-primary)', marginBottom: '18px' }}
          />
          <p
            className="body-large"
            style={{
              color: 'var(--text-primary)',
              lineHeight: 1.7,
              maxWidth: '1000px',
              margin: '0 auto',
            }}
          >
            <strong style={{ color: 'var(--brand-primary)' }}>
              Ciqura Labs
            </strong>{' '}
            is the official industrial training division of{' '}
            <strong>Ciqur24</strong>, an active cybersecurity company operating real-world defense setups.
            Our students gain exclusive, immediate exposure to{' '}
            <strong>real client projects, dedicated corporate internships, and live enterprise-grade
            security operations center (SOC) environments</strong>, ensuring a smooth transition from
            classroom learning to high-salary tech operations in Central India.
          </p>
        </div>

        {/* Core Philosophy */}
        <div
          style={{
            background: 'rgba(0, 255, 209, 0.1)',
            border: '2px solid var(--brand-primary)',
            padding: '40px',
            marginBottom: '60px',
            textAlign: 'center',
          }}
        >
          <FlaskConical
            size={48}
            style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}
          />
          <h3
            className="heading-1"
            style={{ marginBottom: '20px', color: 'var(--brand-primary)' }}
          >
            We Believe in Hacking Labs & Practical Python Automation
          </h3>
          <p
            className="body-large"
            style={{ color: 'var(--text-primary)', lineHeight: 1.7 }}
          >
            At Ciqura Labs Vijay Nagar, we completely prioritize{' '}
            <strong>hands-on sandbox lab infrastructure over basic theoretical lectures</strong>.
            Our professional curriculum is engineered tightly around complex malware logic, system vulnerability hunting (VAPT), production-grade Python scripts, and adaptive machine learning workflows—because the single best path to dominating technology fields is by executing it live.
          </p>
        </div>

        {/* Vision / Mission / Approach */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px',
            marginBottom: '60px',
          }}
        >
          <div className="dark-hover" style={cardStyle}>
            <Shield size={48} style={iconStyle} />
            <h3 className="heading-2">Our Vision</h3>
            <p className="body-medium" style={textMuted}>
              To fundamentally bridge the gap between classical university degrees and enterprise cybersecurity hiring metrics through highly advanced, practical, and security-first industrial training tracks.
            </p>
          </div>

          <div className="dark-hover" style={cardStyle}>
            <Target size={48} style={iconStyle} />
            <h3 className="heading-2">Our Mission</h3>
            <p className="body-medium" style={textMuted}>
              To supply production-ready tech talent across Cybersecurity, Python Systems, Generative AI, Microsoft Cloud Infrastructure, and global GRC Auditor domains by providing actual corporate internship tenure.
            </p>
          </div>

          <div className="dark-hover" style={cardStyle}>
            <Users size={48} style={iconStyle} />
            <h3 className="heading-2">Our Approach</h3>
            <p className="body-medium" style={textMuted}>
              Limited cohort sizes, experienced elite mentors with over 10+ years of active field deployment, industry-standard SIEM tools, and aggressive weekly mock technical interview modules.
            </p>
          </div>
        </div>

        {/* What Makes Us Different */}
        <div style={{ marginBottom: '60px' }}>
          <h3 className="display-medium" style={{ textAlign: 'center', marginBottom: '40px' }}>
            What Makes Our Training Ecosystem Absolute Best
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px',
            }}
          >
            <Feature
              icon={<FlaskConical size={32} style={iconStyle} />}
              title="80% Lab-First Approach"
              text="Ditch the slideshows. Train inside live virtualized penetration testing setups with custom network attack vectors and active defense tracking logs."
            />
            <Feature
              icon={<Lightbulb size={32} style={iconStyle} />}
              title="Ciqur24 Placement Pipeline"
              text="Top performers secure direct technical internship support and potential pre-placement offers (PPOs) at our primary delivery headquarters in Bengaluru."
            />
            <Feature
              icon={<Award size={32} style={iconStyle} />}
              title="Production-Grade Toolsets"
              text="Gain expert control over actual software configurations including Splunk SIEM, Azure Defender, Kali Linux, Wireshark, Burp Suite Pro, and Python frameworks."
            />
          </div>
        </div>

        {/* Specializations */}
        <div
          style={{
            background: 'rgba(0, 255, 209, 0.1)',
            border: '1px solid var(--brand-primary)',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <p className="body-large" style={{ color: 'var(--text-primary)' }}>
            We specialize in providing high-ranking industrial tracks for{' '}
            <strong style={{ color: 'var(--brand-primary)' }}>Cybersecurity & Ethical Hacking</strong>,{' '}
            <strong style={{ color: 'var(--brand-primary)' }}>AI & Machine Learning (AIML) with Python</strong>,{' '}
            <strong style={{ color: 'var(--brand-primary)' }}>Enterprise IT Support & Networking</strong>,{' '}
            <strong style={{ color: 'var(--brand-primary)' }}>Microsoft Cloud Administration</strong>, and{' '}
            <strong style={{ color: 'var(--brand-primary)' }}>Governance, Risk & Compliance (GRC) Auditor Frameworks</strong>.
          </p>
        </div>
      </div>
    </section>
  );
};

/* Helpers */
const cardStyle = {
  background: 'var(--bg-secondary)',
  padding: '40px',
  border: '1px solid var(--border-subtle)',
  transition: 'all 0.4s ease-in-out',
};

const iconStyle = {
  color: 'var(--brand-primary)',
  marginBottom: '20px',
};

const textMuted = {
  color: 'var(--text-secondary)',
};

const Feature = ({ icon, title, text }) => (
  <div className="dark-hover" style={cardStyle}>
    {icon}
    <h4 className="heading-3" style={{ marginBottom: '12px' }}>{title}</h4>
    <p className="body-medium" style={textMuted}>{text}</p>
  </div>
);

export default About;